import Dropbox from 'dropbox';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DropboxFile } from '../../models/dropboxFile';
import { isAudioFile } from '../../models/fileExtensions';
import { StorageProviderKey } from '../../models/storageProviderKeys';
import Constants, { Keys, messages } from '../../settings';
import compress, { decompress } from '../../utilities/flateCompressDecompress';
import { shouldUseRedirect } from '../../utilities/platform';
import IStorageProvider from '../storage/istorageProvider';
import { Context } from '../_globalContext/context';
import * as dropboxAuth from './dropboxAuth';
import handleDropboxError from './handleDropboxError';

/**
 * Re-exported because it used to live here and is imported from here. The
 * flow that needs it now owns it.
 */
export const appKey = dropboxAuth.appKey;

export interface IDropboxContext {
  dbx: Dropbox.Dropbox;
  /**
   * Signing in. On iOS this navigates the whole page away and the returned
   * promise never settles - see dropboxAuth. Only callers passing
   * `allowRedirect` can take that path, because being thrown out of the app is
   * a reasonable thing to happen when you press "Dropbox Authorize" and not a
   * reasonable thing to happen when you press play.
   */
  authorize(options?: {
    allowRedirect?: boolean;
    intent?: dropboxAuth.DropboxIntent;
  }): Promise<void>;
  /** Whether authorize() can complete here, or only by navigating away. */
  canAuthorizeInPlace: boolean;
  /** A sign-in is in flight. Buttons show it and refuse a second press. */
  isAuthorizing: boolean;
  logout(): void;
  account: Dropbox.users.FullAccount | undefined;

  getUserDrive(path: string | undefined, resultsPerPage: number): Promise<DropboxIterator>;
  getAllTracksRecursive(path: string | undefined, ):
    Promise<DropboxTypes.files.FileMetadataReference[]>;

  errorHandler(err: any): void;
}
export const DropboxContext = React.createContext({} as IDropboxContext);

interface Props {
  children: React.ReactNode;
}

export interface DropboxIterator {
  files: Dropbox.files.ListFolderResult;
  next(prev: Dropbox.files.ListFolderResult): Promise<DropboxIterator>;
}
export default function DropboxService({
  children,
}: Props) {

  const [dbx] = useState(() => new Dropbox.Dropbox({
    clientId: appKey,
    fetch,
  }));

  const sp = useRef<IStorageProvider>();
  const popupCallback = useRef<(() => void) | undefined>();
  /**
   * Set the instant the popup says it is done, before anything is awaited.
   * The close-watcher below has to be able to tell "the user gave up" from
   * "it finished and closed itself", and those are microseconds apart.
   */
  const popupCompleted = useRef(false);

  const ctx = useContext(Context);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const [account, setAccount] = useState<Dropbox.users.FullAccount>();

  useEffect(function debug() {
    if (process.env.NODE_ENV === 'development') {
      (window as any)['dbx'] = dbx;
    }
  }, [dbx]);
  useEffect(function registerAsStorageProvider() {
    ctxRef.current.storageProviderMap[StorageProviderKey.Dropbox] = sp;
  }, []);

  /**
   * Puts a fresh access token on the SDK client and hands it back.
   *
   * The SDK holds one token as a field and has no idea it can expire, so the
   * only way to keep it honest is to set it again before it is used. Cheap:
   * getFreshAccessToken does nothing at all unless expiry is close.
   */
  const ensureFreshToken = useCallback(async function () {
    const accessToken = await dropboxAuth.getFreshAccessToken();

    if (accessToken) {
      dbx.setAccessToken(accessToken);
    }

    return accessToken;
  }, [dbx]);

  const adoptStoredToken = useCallback(async function () {
    const accessToken = await ensureFreshToken();

    if (!accessToken) {
      return false;
    }

    setAccount(await dbx.usersGetCurrentAccount());

    return true;
  }, [dbx, ensureFreshToken]);

  useEffect(function loadTokenFromStorage() {
    adoptStoredToken().then(
      function actOnRedirectIntent(signedIn) {
        if (!signedIn) {
          return;
        }

        // A redirect sign-in lands back here, not in the component that
        // started it - /dropbox-redirect renders outside the service tree, so
        // by the time this service exists again the caller that asked for the
        // sign-in is long gone. The intent it left behind is how "set Dropbox
        // as the storage provider" survives that.
        if (dropboxAuth.takeIntent().configureProvider) {
          ctxRef.current.configureStorageProvider(StorageProviderKey.Dropbox);
        }
      },
      function ignoreStartupFailure() {
        // No token, a dead one, or Dropbox unreachable. Not being signed in to
        // Dropbox is an ordinary state and startup must not stall on it.
      },
    );
  }, [adoptStoredToken]);

  useEffect(function listenForPopupCompletion() {

    async function messageHandler(e: MessageEvent) {
      if (e.origin !== window.location.origin
        || !e.data
        || e.data[dropboxAuth.popupDoneMessage] !== true
      ) {
        return;
      }

      popupCompleted.current = true;

      // The popup redeemed the code and wrote the token store itself, so
      // there is nothing to carry in the message. Just read what it left.
      await adoptStoredToken();

      if (popupCallback.current) {
        popupCallback.current();
      }
    }

    window.addEventListener('message', messageHandler, false);

    return function () {
      window.removeEventListener('message', messageHandler, false);
    };
  }, [adoptStoredToken]);

  const doAuthorize = useCallback(async function (options?: {
    allowRedirect?: boolean;
    intent?: dropboxAuth.DropboxIntent;
  }): Promise<void> {

    if (dropboxAuth.willRedirect(options && options.allowRedirect)) {
      await dropboxAuth.beginRedirect(options && options.intent);

      // The page is on its way to Dropbox. Hand back a promise that never
      // settles so no caller runs a .then() against a document being torn
      // down - same contract gapiService.authorize has on this path.
      return new Promise<void>(function neverSettles() { });
    }

    popupCompleted.current = false;

    const popup = await dropboxAuth.beginPopup(options && options.intent);

    return new Promise<void>(function waitForPopup(resolve, reject) {

      const poll = setInterval(function checkIfClosed() {
        // `window.closed` used to be the test here, which asks whether *this*
        // window has closed - it never has, so a cancelled sign-in hung
        // forever instead of rejecting.
        if (!popup.closed || popupCompleted.current) {
          return;
        }

        clearInterval(poll);
        popupCallback.current = undefined;
        reject(new Error('Dropbox sign-in was cancelled.'));
      }, 300);

      popupCallback.current = function popupFinished() {
        clearInterval(poll);
        popupCallback.current = undefined;

        // Consume the intent so it cannot fire a second time on the next
        // mount. On this path the caller is still alive and acts on the
        // resolved promise itself.
        dropboxAuth.takeIntent();

        resolve();
      };
    });
  }, []);

  const [isAuthorizing, setAuthorizing] = useState(false);
  const authorizeInFlight = useRef<Promise<void>>();

  /**
   * One sign-in at a time - same shape as oneDriveService.authorize, for the
   * same reason. A second press here would open a second popup that the first
   * one's close-watcher knows nothing about. The second caller gets the first
   * promise instead, and isAuthorizing lets the button say so.
   *
   * beginPopup's window.open still runs synchronously inside the click: this
   * wrapper adds no await in front of doAuthorize.
   */
  const authorize = useCallback(function (options?: {
    allowRedirect?: boolean;
    intent?: dropboxAuth.DropboxIntent;
  }): Promise<void> {

    if (authorizeInFlight.current) {
      return authorizeInFlight.current;
    }

    setAuthorizing(true);

    authorizeInFlight.current = doAuthorize(options).finally(function settled() {
      authorizeInFlight.current = undefined;
      setAuthorizing(false);
    });

    return authorizeInFlight.current;
  }, [doAuthorize]);

  const logout = useCallback(function () {

    function completeLogout() {

      setAccount(undefined);
      dbx.authTokenRevoke();

      dropboxAuth.clearTokens();
    }

    if (ctxRef.current.storageProvider.current === sp.current) {
      ctxRef.current.configureStorageProvider();
      ctxRef.current.spLogoutHandling().then(completeLogout);
    }
    else {
      completeLogout();
    }
  }, [dbx]);

  const getUserDrive = useCallback(async function (
    path: string | undefined = '',
    resultsPerPage: number,
    options?: any,
  ): Promise<DropboxIterator> {

    await ensureFreshToken();

    let files = await dbx.filesListFolder({
      path,
      limit: Math.max(100, resultsPerPage),
      ...(options || {}),
    });

    async function next(
      prevResults: Dropbox.files.ListFolderResult,
    ): Promise<DropboxIterator> {
      return {
        files: await dbx.filesListFolderContinue({
          cursor: prevResults.cursor,
        }),
        next,
      };
    }

    // Dropbox doesn't seem to return results = rpp
    while (files.has_more && files.entries.length < resultsPerPage) {
      const iter = await next(files);
      const entries = files.entries.concat(iter.files.entries);
      files = iter.files;
      files.entries = entries;
    }

    return {
      files,
      next,
    };
  }, [dbx, ensureFreshToken]);

  const getAllTracksRecursive = useCallback(async function (
    path: string | undefined = '',
  ) {
    const resultsPerPage = 2000;

    ctxRef.current.setRunningTask('getDropboxTracksRecursive');
    const results: DropboxFile[] = [];

    try {
      let iter = await getUserDrive(
        path,
        resultsPerPage,
        {
          recursive: true,
          include_non_downloadable_files: false,
        }
      );

      let tracks = iter.files.entries.filter(x => isAudioFile(x.name));

      results.push(...tracks);

      while (iter.files.has_more) {
        iter = await iter.next(iter.files);
        tracks = iter.files.entries.filter(x => isAudioFile(x.name));

        results.push(...tracks);
      }
    } finally {
      ctxRef.current.removeRunningTask('getDropboxTracksRecursive');
    }

    return results as Dropbox.files.FileMetadataReference[];
  }, [getUserDrive]);

  const handleError = useMemo(
    () => handleDropboxError(ctxRef, logout),
    [logout],
  );

  const putFile = useCallback(async function (
    path: string,
    fileData: any,
    mute = false,
  ) {
    try {
      await ensureFreshToken();

      const compressed = compress(fileData, ctxRef.current.flate);

      await dbx.filesUpload({
        path,
        mode: {
          '.tag': 'overwrite'
        },
        mute,
        contents: compressed,
      });
    } catch (err) {
      handleError(err);
    }
  }, [dbx, ensureFreshToken, handleError]);

  const getOrCreateDataFile = useCallback(async function (
    path: string,
    defaultData: any,
  ) {
    try {
      await ensureFreshToken();

      const res = await dbx.filesDownload({
        path
      });

      const buffer = await (res as any).fileBlob.arrayBuffer();

      return decompress(buffer, ctxRef.current.flate);
    }
    catch (err) {
      try {
        await handleError(err, async () => {
          // No need for user to wait on this, 
          // we do want to handle and/or display message on error though
          putFile(path, defaultData);
        });

        return defaultData;
      }
      catch (innerErr) {
        await handleError(err);
        return Promise.reject();
      }
    }
  }, [dbx, ensureFreshToken, handleError, putFile]);

  useEffect(function updateStorageProvider() {
    sp.current = {
      name: StorageProviderKey.Dropbox,
      load: (fileName: string, defaultData: any) => {
        if (account) {
          return getOrCreateDataFile(`/${Constants.DataFolder}/${fileName}`, defaultData);
        }
        else {
          ctxRef.current.configureStorageProvider();
          return Promise.reject(messages(Keys.storageProviderNotLoggedIn));
        }
      },
      saveAppData: (fileName: string, fileData: any) => {
        if (account) {
          return putFile(`/${Constants.DataFolder}/${fileName}`, fileData);
        }
        else {
          ctxRef.current.configureStorageProvider();
          return Promise.reject(messages(Keys.storageProviderNotLoggedIn));
        }
      },
    };
  }, [account, getOrCreateDataFile, putFile]);

  /**
   * Here we proxy all method invocations on the dropbox object when used elsewhere in the app
   * This way we can ensure an access token is configured and display the appropriate message
   * if it is not.
   */
  const methodProxy = useMemo(() => ({
    apply: function (target: any, thisArg: any, argumentsList: any) {
      // The store, not dbx.getAccessToken(): the SDK's copy is whatever was
      // last written to it, which after a logout in another tab is a token
      // that no longer exists.
      if (!dropboxAuth.readTokens()) {
        ctxRef.current.callSnackbar(messages(Keys.dropboxNotLoggedIn), false);

        return Promise.reject();
      }

      // Every SDK call the app makes arrives here, which makes this the one
      // place that can promise the token about to be used has not expired.
      return ensureFreshToken().then(function callWithFreshToken() {
        return target.apply(dbx, argumentsList);
      });
    }
  }), [dbx, ensureFreshToken]);
  const handler = useMemo(() => ({
    get: function (target: Dropbox.Dropbox, prop: string, _receiver: any) {

      const targetProp = (target as any)[prop];

      return typeof targetProp === 'object' 
        ? new Proxy(targetProp, methodProxy)
        : targetProp
        ;

      // return prop === 'filesGetTemporaryLink'
      //   ? new Proxy(targetProp, getTemporaryLinkProxy)
      //   : targetProp;
    },
  }), [methodProxy]);

  const dbxProxy = useMemo(() => new Proxy(dbx, handler), [dbx, handler]);

  const ctxValue = useMemo(() => ({
    dbx: dbxProxy,
    authorize,
    canAuthorizeInPlace: !shouldUseRedirect(),
    isAuthorizing,
    logout,
    account,

    getUserDrive,
    getAllTracksRecursive,

    errorHandler: handleError,
  }), [
    dbxProxy,
    authorize,
    isAuthorizing,
    logout,
    account,
    getUserDrive,
    getAllTracksRecursive,
    handleError,
  ]);

  return (
    <DropboxContext.Provider value={ctxValue}>
      {children}
    </DropboxContext.Provider>
  );
}
